import { parseCookies, setCookie, destroyCookie } from 'nookies'
import { GetServerSidePropsContext } from 'next'
import { ParsedUrlQuery } from 'querystring'

import { SESSION_TIMEOUT } from '@/lib/constants'

// Marketing appends these to the free trial link to attribute a signup to its
// campaign. They are also the only keys the backend's `utmCampaign` accepts.
export const UTM_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'] as const

export type UtmParam = typeof UTM_PARAMS[number]

export type UtmCampaign = Partial<Record<UtmParam, string>>

export type SegmentCampaign = Partial<Record<'name' | 'source' | 'medium' | 'term' | 'content', string>>

// The backend validates each value against this length and rejects the whole
// login / sign up with a 400 when one is longer, so values are capped up front.
export const UTM_MAX_LENGTH = 255

const SEGMENT_CAMPAIGN_KEYS: Record<UtmParam, keyof SegmentCampaign> = {
  utm_source: 'source',
  utm_medium: 'medium',
  utm_campaign: 'name',
  utm_term: 'term',
  utm_content: 'content',
}

const hasValues = (campaign: object) => Object.keys(campaign).length > 0

const readValue = (value?: string | string[]) => {
  const first = Array.isArray(value) ? value[0] : value
  return typeof first === 'string' ? first.trim().slice(0, UTM_MAX_LENGTH) : ''
}

/**
 * Picks the UTM parameters out of a query string or cookie jar.
 * @param source The parsed query or cookies to read from
 * @returns The UTM parameters that carry a value, trimmed and capped at `UTM_MAX_LENGTH`
 */
export const pickUtmCampaign = (source: ParsedUrlQuery = {}): UtmCampaign => (
  UTM_PARAMS.reduce<UtmCampaign>((campaign, param) => {
    const value = readValue(source[param])
    return value ? { ...campaign, [param]: value } : campaign
  }, {})
)

/**
 * Stores each UTM parameter of a free trial link in its own cookie, next to the
 * free trial id, so they survive the login / create account redirects.
 * @param query The query of the free trial link
 * @param ctx The server context, omitted on the client
 */
export const storeUtmCampaign = (query: ParsedUrlQuery, ctx?: GetServerSidePropsContext) => {
  const campaign = pickUtmCampaign(query)

  // A link without UTMs is a direct visit rather than a new campaign touch, so it
  // keeps the attribution an earlier campaign link stored. A link with UTMs
  // replaces the whole set, so two campaigns are never mixed into one.
  if (!hasValues(campaign)) {
    return
  }

  UTM_PARAMS.forEach(param => {
    if (campaign[param]) {
      setCookie(ctx ?? {}, param, campaign[param], {
        maxAge: SESSION_TIMEOUT,
        path: '/',
        secure: true,
        sameSite: 'lax',
      })
    } else {
      destroyCookie(ctx ?? {}, param, { path: '/' })
    }
  })
}

/**
 * Reads the UTM parameters stored from a free trial link.
 * @param ctx The server context, omitted on the client
 * @returns The stored UTM parameters, or `null` when none are stored
 */
export const fetchUtmCampaign = (ctx?: GetServerSidePropsContext): UtmCampaign | null => {
  const campaign = pickUtmCampaign(parseCookies(ctx))
  return hasValues(campaign) ? campaign : null
}

/**
 * Removes every stored UTM parameter.
 * @param ctx The server context, omitted on the client
 */
export const destroyUtmCampaign = (ctx?: GetServerSidePropsContext) => {
  UTM_PARAMS.forEach(param => destroyCookie(ctx ?? {}, param, { path: '/' }))
}

/**
 * Maps UTM parameters to Segment's `context.campaign` shape: the `utm_` prefix
 * is dropped and `utm_campaign` becomes `name`.
 * @param campaign The UTM parameters to map
 * @returns The Segment campaign
 */
export const toSegmentCampaign = (campaign: UtmCampaign): SegmentCampaign => (
  UTM_PARAMS.reduce<SegmentCampaign>((segmentCampaign, param) => (
    campaign[param]
      ? { ...segmentCampaign, [SEGMENT_CAMPAIGN_KEYS[param]]: campaign[param] }
      : segmentCampaign
  ), {})
)

/**
 * Adds the stored UTM parameters to a login or sign up payload as `utmCampaign`.
 * @param payload The request payload
 * @returns The payload with `utmCampaign`, or the payload untouched when none are stored
 */
export const withUtmCampaign = <T extends object>(payload: T): T & { utmCampaign?: UtmCampaign } => {
  const utmCampaign = fetchUtmCampaign()
  return utmCampaign ? { ...payload, utmCampaign } : payload
}
